import React from "react";
import PropTypes from "prop-types";
import useAutocomplete from "@mui/material/useAutocomplete";
import { Popper } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { US_STATES } from "../../Constants";

function StateDropdown({
  label,
  value,
  onChange,
  placeholder = "",
  disabled = false,
  className = "",
}) {
  // Custom filter that matches either the state's name or abbreviation
  const filterOptions = (options, { inputValue }) => {
    const normalizedInput = inputValue.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(normalizedInput) ||
        option.abbreviation.toLowerCase().includes(normalizedInput)
    );
  };

  const {
    getRootProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    setAnchorEl,
    anchorEl,
    popupOpen,
  } = useAutocomplete({
    options: US_STATES,
    value,
    getOptionLabel: (option) => option.label,
    onChange: (event, newValue) => onChange(newValue),
    filterOptions,
    disabled,
    autoHighlight: true,
    autoSelect: true,
  });

  return (
    <div {...getRootProps()} className={`flex flex-col ${className}`}>
      {label && (
        <label
          className="py-2 w-5/12 flex items-center text-sm"
          style={{
            lineHeight: "1.2",
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
        >
          {label}
        </label>
      )}

      <div
        ref={setAnchorEl}
        className={`flex-grow text-sm p-1 ${
          disabled ? "bg-gray-100 pointer-events-none" : ""
        }`}
      >
        <input
          {...getInputProps()}
          placeholder={placeholder}
          disabled={disabled}
          tabIndex={0}
          className="w-full outline-none bg-transparent text-sm text-gray-800"
        />
      </div>

      <Popper
        open={popupOpen && groupedOptions.length > 0}
        anchorEl={anchorEl}
        style={{ zIndex: 9999 }}
      >
        <ul
          {...getListboxProps()}
          className="w-full bg-white shadow-lg overflow-auto"
          style={{ width: anchorEl?.clientWidth || 300, maxHeight: 300 }}
        >
          {groupedOptions.map((option, index) => {
            const optionProps = getOptionProps({ option, index });
            return (
              <li
                {...optionProps}
                key={option.abbreviation}
                className="px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100"
              >
                {option.label} ({option.abbreviation})
              </li>
            );
          })}
        </ul>
      </Popper>
    </div>
  );
}

StateDropdown.propTypes = {
  label: PropTypes.string,
  value: PropTypes.object,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export default StateDropdown;
